import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { activitiesListRequest } from '@api/@angular/common.gen';
import type { ActivitiesListResponse } from '@api';

import { emptyOnUnsafeInput } from '@shared/input-safety/empty-on-unsafe-input';
import { emptyActivitiesPage } from '@shared/services/api/activities-list.api.service';

import { sendCancellable } from './send-cancellable';

const DEFAULT_PAGE_LIMIT = 100;

export type GetActivitiesOptions = {
  tagPath?: string[];
  searchTerm?: string;
  cursor?: string;
  limit?: number;
  activityType?: string;
  createdByMe?: boolean;
  /** With `createdByMe`, also returns colleagues' activities shared with the caller's tenant. */
  includeTenantShared?: boolean;
  favoritesOnly?: boolean;
  favoritesFirst?: boolean;
  sortBy?: string;
  sortDir?: string;
  durationValue?: number;
  durationUnit?: string;
  createdFrom?: string;
  createdTo?: string;
  /** Aborting cancels the request in flight. */
  signal?: AbortSignal;
};

/** API adapter for the unified `GET /v1/activities` list endpoint (the activity-browser one, not the shared one). */
@Injectable({ providedIn: 'root' })
export class ActivitiesListApiService {
  readonly #httpClient = inject(HttpClient);

  /**
   * @returns The page, or an empty page with a total of 0 when the client or Kt.Api rejects the query as unsafe input.
   *   Every other failure rejects, including an abort.
   */
  list(options: GetActivitiesOptions = {}): Promise<ActivitiesListResponse> {
    const searchTerm = options.searchTerm?.trim();

    const request = activitiesListRequest({
      httpClient: this.#httpClient,
      query: {
        limit: options.limit ?? DEFAULT_PAGE_LIMIT,
        cursor: options.cursor,
        tagPath: options.tagPath,
        searchTerm: searchTerm && searchTerm.length > 0 ? searchTerm : undefined,
        activityType: options.activityType,
        ...(options.createdByMe ? { createdByMe: true } : {}),
        ...(options.includeTenantShared ? { includeTenantShared: true } : {}),
        ...(options.favoritesOnly ? { favoritesOnly: true } : {}),
        ...(options.favoritesFirst ? { favoritesFirst: true } : {}),
        sortBy: options.sortBy,
        sortDir: options.sortDir,
        durationValue: options.durationValue,
        durationUnit: options.durationUnit,
        createdFrom: options.createdFrom,
        createdTo: options.createdTo,
      },
    });

    return emptyOnUnsafeInput(
      () => sendCancellable<ActivitiesListResponse>(this.#httpClient, request, options.signal),
      emptyActivitiesPage,
    );
  }
}
